/**
 * Vue I18n 国际化入口
 * - 公共文案：locales/common
 * - 各模块文案：locales/modules/{moduleName}
 * - 默认使用简体中文，用户可通过语言切换组件显式切换为 en-US
 */
import Vue from 'vue';
import VueI18n from 'vue-i18n';

import commonEn from './locales/common/en-US';
import commonZh from './locales/common/zh-CN';

import cockpitManageEn from './locales/modules/cockpitManage/en-US';
import cockpitManageZh from './locales/modules/cockpitManage/zh-CN';
import dataCenterEn from './locales/modules/dataCenter/en-US';
import dataCenterZh from './locales/modules/dataCenter/zh-CN';
import financeCenterEn from './locales/modules/financeCenter/en-US';
import financeCenterZh from './locales/modules/financeCenter/zh-CN';
import operateManageEn from './locales/modules/operateManage/en-US';
import operateManageZh from './locales/modules/operateManage/zh-CN';
import operationCenterEn from './locales/modules/operationCenter/en-US';
import operationCenterZh from './locales/modules/operationCenter/zh-CN';
import showCardEn from './locales/modules/showCard/en-US';
import showCardZh from './locales/modules/showCard/zh-CN';

// Element UI 组件库语言包（与 vue-i18n 共用，key 为 el.xxx）
import elementEn from 'element-ui/lib/locale/lang/en';
import elementZh from 'element-ui/lib/locale/lang/zh-CN';

Vue.use(VueI18n);

const LOCALE_KEY = 'app_locale';
const FALLBACK_LOCALE = 'zh-CN';
const SUPPORTED_LOCALES = ['en-US', 'zh-CN'];

function normalizeLocale(locale) {
  return SUPPORTED_LOCALES.includes(locale) ? locale : '';
}

/**
 * 根据当前语言获取列宽度
 * 中文直接使用 width，其他语言优先使用对应语言的 Width（如 enWidth），没有则回退到 width
 *
 * @param {string} locale - 当前语言，如 'zh-CN'、'en-US'
 * @param {number} width - 默认宽度（中文使用）
 * @param {number} [enWidth] - 英文宽度（可选）
 * @returns {number} 列宽度
 */
export function getColumnWidth(locale, width, enWidth) {
  if (locale === 'zh-CN') {
    return width;
  }
  return enWidth || width;
}

// 挂载到 Vue 原型，所有组件可通过 this.$getColumnWidth 使用
Vue.prototype.$getColumnWidth = function(width, enWidth) {
  return getColumnWidth(this.$i18n.locale, width, enWidth);
};

function mergeMessages(common, modules, element) {
  return {
    common: common,
    el: element?.el ?? {},
    cockpitManage: modules.cockpitManage,
    dataCenter: modules.dataCenter,
    financeCenter: modules.financeCenter,
    operateManage: modules.operateManage,
    operationCenter: modules.operationCenter,
    showCard: modules.showCard,
  };
}

const messages = {
  'en-US': mergeMessages(commonEn, {
    cockpitManage: cockpitManageEn,
    dataCenter: dataCenterEn,
    financeCenter: financeCenterEn,
    operateManage: operateManageEn,
    operationCenter: operationCenterEn,
    showCard: showCardEn,
  }, elementEn),
  'zh-CN': mergeMessages(commonZh, {
    cockpitManage: cockpitManageZh,
    dataCenter: dataCenterZh,
    financeCenter: financeCenterZh,
    operateManage: operateManageZh,
    operationCenter: operationCenterZh,
    showCard: showCardZh,
  }, elementZh),
};

function getStoredLocale() {
  try {
    const stored = normalizeLocale(localStorage.getItem(LOCALE_KEY));
    if (stored) return stored;
  } catch {
    // ignore storage access failures
  }
  return FALLBACK_LOCALE;
}

const i18n = new VueI18n({
  locale: getStoredLocale(),
  fallbackLocale: FALLBACK_LOCALE,
  messages,
  silentTranslationWarn: true,
});

/**
 * 切换语言并持久化
 * @param {string} locale - 如 'en-US' | 'zh-CN'
 */
export function setLocale(locale) {
  if (messages[locale]) {
    i18n.locale = locale;
    try {
      localStorage.setItem(LOCALE_KEY, locale);
    } catch (_) {}
    return locale;
  }
  return i18n.locale;
}

/**
 * 获取当前语言
 */
export function getLocale() {
  return i18n.locale;
}

/**
 * 支持的语言列表（便于后续扩展）
 */
export const supportedLocales = [
  { value: 'en-US', label: 'English' },
  { value: 'zh-CN', label: '简体中文' },
];

export default i18n;
